/**
 * The `CurrentIds` port: route identity as an injected capability.
 *
 * Route identity is a property of *where* a screen is mounted, so it belongs to
 * whoever mounts it. A package screen reading `next/router` directly would be
 * unmountable anywhere else — which is exactly what the app already needs, and
 * already solves app-side with an override context (the linked-context canvas
 * preview renders session-page components off `/scribe/session/{id}`).
 *
 * So this door carries the *contract* and the injection seam; the app registers
 * an implementation over its router and that override context. Same shape as
 * the i18n adapter: one module-scope slot, registration at module scope from app
 * wiring, so ordering holds by construction.
 *
 * Recording and offline session ids stay app-side: they read a feature flag and
 * a jotai atom, neither of which is route identity, and no package reads them.
 */

/**
 * What the app registers. Both members mirror the app's own hooks rather than
 * reimplementing them, so a call site that moves onto this door is reading the
 * same value it read before.
 */
export interface CurrentIdsAdapter {
  /**
   * The session id for the current route, `''` when the route has none. Must
   * honour whatever override the app layers over the router.
   */
  readonly useCurrentSessionId: () => string;
  /**
   * Non-React accessor for modules that are not components — request
   * interceptors and other leaves. `undefined` when the route has no session
   * id, or when the router is not ready yet: those are different states and the
   * app's own accessor distinguishes them.
   */
  readonly getCurrentSessionId: () => string | undefined;
}

let adapter: CurrentIdsAdapter | null = null;

/** Register the implementation ONCE at module scope, before anything renders. */
export function registerCurrentIdsAdapter(next: CurrentIdsAdapter): void {
  adapter = next;
}

/** Whether an implementation has been registered. */
export function hasCurrentIdsAdapter(): boolean {
  return adapter !== null;
}

/** Test-only: drop the registered adapter between tests. */
export function __resetCurrentIdsAdapterForTests(): void {
  adapter = null;
}

/**
 * The session id for the current route.
 *
 * The no-adapter fallback is `''` — the value the app's own hook returns off a
 * session route, so an unregistered door reads as "no session here" rather than
 * as a crash. It is a hook so the app's implementation can be one, and it is
 * called unconditionally for the same reason.
 */
export function useCurrentSessionId(): string {
  if (adapter === null) return '';
  return adapter.useCurrentSessionId();
}

/**
 * Non-React accessor. Resolved per call, so a module that loads before app
 * wiring still sees the adapter once registered.
 *
 * The no-adapter fallback is `undefined`, mirroring the app accessor's own
 * no-route value — not `''`, which callers reading this as "is there a session"
 * would then have to special-case in a second way.
 */
export function getCurrentSessionId(): string | undefined {
  if (adapter === null) return undefined;
  return adapter.getCurrentSessionId();
}
